
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import CustomDropdown from "@/components/admin/CustomDropdown";

const statusColors: Record<string, string> = {
  completed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700",
};

export default function AdminPaymentsPage() {
  const { token } = useAuthStore();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const paymentsPerPage = 30;

  useEffect(() => {
    if (token) {
      fetchPayments();
    }
  }, [token, currentPage]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getPayments(token!, {
        page: currentPage,
        limit: paymentsPerPage,
      });
      // Handle wrapped response
      const paymentsList = data.payments || data || [];
      setPayments(paymentsList);
      setTotalPayments(data.total || paymentsList.length);
    } catch (error: any) {
      console.error("Error fetching payments:", error);
      toast.error(error.response?.data?.detail || "Failed to load payments");
      setPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalPayments / paymentsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePaymentStatusUpdate = async (paymentId: number, newStatus: string) => {
    try {
      await adminApi.updatePaymentStatus(paymentId, newStatus, token!);
      toast.success(`Payment status updated to ${newStatus.toUpperCase()}`);
      // Refresh payments list
      await fetchPayments();
    } catch (error: any) {
      console.error("Error updating payment status:", error);
      toast.error(error.response?.data?.detail || "Failed to update payment status");
    }
  };


  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0">
            <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-1" />
            <div className="h-3 w-56 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="w-1/2 h-9 bg-gray-200 rounded-full animate-pulse" />
          <div className="flex-shrink-0 h-9 w-24 bg-gray-200 rounded-lg animate-pulse ml-auto" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-7 w-32 bg-gray-200 rounded animate-pulse" />
                </div>
                <div className="h-12 w-12 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <th key={i} className="px-4 py-2.5">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6].map((j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const filteredPayments = payments.filter((payment) =>
    payment.razorpay_payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.order?.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.order?.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `pay-${payment.id}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4">
      {/* Header and Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-shrink-0">
          <h1 className="text-xl font-bold font-playfair mb-0.5">Payments</h1>
          <p className="text-xs text-gray-500">Monitor and manage payment transactions</p>
        </div>
        <div className="w-1/2 relative ml-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search payments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Payment ID
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Order #
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {searchTerm ? "No payments match your search" : "No payments found"}
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-900">
                        {payment.razorpay_payment_id || `PAY-${payment.id}`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-sm text-gray-900">
                        {payment.order?.order_number || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-sm text-gray-900">
                        ₹{parseFloat(payment.amount || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600 capitalize">
                        {payment.payment_method || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <CustomDropdown
                        value={payment.status || "pending"}
                        onChange={(newStatus) => handlePaymentStatusUpdate(payment.id, newStatus)}
                        options={[
                          { value: "pending", label: "PENDING", className: "bg-yellow-100 text-yellow-700" },
                          { value: "processing", label: "PROCESSING", className: "bg-blue-100 text-blue-700" },
                          { value: "completed", label: "COMPLETED", className: "bg-green-100 text-green-700" },
                          { value: "failed", label: "FAILED", className: "bg-red-100 text-red-700" },
                          { value: "refunded", label: "REFUNDED", className: "bg-gray-100 text-gray-700" },
                        ]}
                        className="w-36"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {payment.created_at
                          ? new Date(payment.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * paymentsPerPage + 1} to{" "}
            {Math.min(currentPage * paymentsPerPage, totalPayments)} of {totalPayments} payments
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? "bg-gray-900 text-white"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
